import React from 'react';

interface PhoneNumberProps {
  phoneNumber?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface CountryData {
  code: string;
  flag: string;
  format: (number: string) => string;
}

// Country code mapping with flags and formatting rules
const COUNTRY_CODES: Record<string, CountryData> = {
  '1': {
    code: '+1',
    flag: '🇺🇸',
    format: (n) => {
      // US/Canada: +1 (XXX) XXX-XXXX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length === 11) {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }
      return n;
    },
  },
  '46': {
    code: '+46',
    flag: '🇸🇪',
    format: (n) => {
      // Sweden: +46 XX XXX XX XX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length === 11) {
        return `${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
      }
      return n;
    },
  },
  '44': {
    code: '+44',
    flag: '🇬🇧',
    format: (n) => {
      // UK: +44 XXXX XXX XXX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length >= 11) {
        return `${cleaned.slice(2, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
      }
      return n;
    },
  },
  '49': {
    code: '+49',
    flag: '🇩🇪',
    format: (n) => {
      // Germany: +49 XXX XXXXXXX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length >= 11) {
        return `${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
      }
      return n;
    },
  },
  '33': {
    code: '+33',
    flag: '🇫🇷',
    format: (n) => {
      // France: +33 X XX XX XX XX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length >= 11) {
        return `${cleaned.slice(2, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
      }
      return n;
    },
  },
  '47': {
    code: '+47',
    flag: '🇳🇴',
    format: (n) => {
      // Norway: +47 XXX XX XXX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length >= 10) {
        return `${cleaned.slice(2, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7)}`;
      }
      return n;
    },
  },
  '45': {
    code: '+45',
    flag: '🇩🇰',
    format: (n) => {
      // Denmark: +45 XX XX XX XX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length >= 10) {
        return `${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
      }
      return n;
    },
  },
  '358': {
    code: '+358',
    flag: '🇫🇮',
    format: (n) => {
      // Finland: +358 XX XXX XXXX
      const cleaned = n.replace(/\D/g, '');
      if (cleaned.length >= 11) {
        return `${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
      }
      return n;
    },
  },
};

const parsePhoneNumber = (phoneNumber: string): {countryCode: string; flag: string; formattedNumber: string} | null => {
  if (!phoneNumber) return null;

  // Remove whitespace and common separators
  const cleaned = phoneNumber.trim().replace(/[\s\-().]/g, '');

  // Try to match country codes (sorted by length desc to match longer codes first)
  const countryCodes = Object.keys(COUNTRY_CODES).sort((a, b) => b.length - a.length);

  for (const code of countryCodes) {
    if (cleaned.startsWith(`+${code}`) || cleaned.startsWith(code)) {
      const countryData = COUNTRY_CODES[code];
      const formattedNumber = countryData.format(cleaned);
      return {
        countryCode: countryData.code,
        flag: countryData.flag,
        formattedNumber,
      };
    }
  }

  // If no country code detected, return as-is
  return {
    countryCode: '',
    flag: '🌍',
    formattedNumber: phoneNumber,
  };
};

const sizeClasses = {
  sm: {
    flag: 'text-base',
    code: 'text-xs',
    number: 'text-xs',
  },
  md: {
    flag: 'text-lg',
    code: 'text-sm',
    number: 'text-sm',
  },
  lg: {
    flag: 'text-xl',
    code: 'text-base',
    number: 'text-base',
  },
};

export function PhoneNumber({phoneNumber, className = '', size = 'md'}: PhoneNumberProps) {
  if (!phoneNumber) return null;

  const parsed = parsePhoneNumber(phoneNumber);
  if (!parsed) return null;

  const sizes = sizeClasses[size];

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span className={sizes.flag} role="img" aria-label="Country flag">
        {parsed.flag}
      </span>
      {parsed.countryCode && <span className={`${sizes.code} font-medium text-gray-600 dark:text-gray-400`}>{parsed.countryCode}</span>}
      <span className={`${sizes.number} font-medium text-gray-900 dark:text-gray-100`}>{parsed.formattedNumber}</span>
    </div>
  );
}
